/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology,
* Texas A&M Engineering Experiment Station, The Texas A&M
* University System.
*
* This source code/schema and any derivative work is Texas
* A&M Engineering Experiment Station intellectual property
* and protected by copyright law. Usage is restricted solely
* to the FARM Animal Care and Environmental Stewardship
* programs administered by the National Milk Producers
* Federation. Dissemination of this source code and/or
* derivative work creation is strictly prohibited without
* express written permission from the Texas A&M Engineering
* Experiment Station.
*/
import { createMuiTheme, responsiveFontSizes } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/styles";
import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { BrowserRouter as Router, Route } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import App from "./App";
import configureStore from "./db/configureStore";
import "./index.css";
import OAuth from "./react/container/OAuth";
import grey from "@material-ui/core/colors/grey";
import { ServiceWorkerProvider } from './react/container/ServiceWorkerContext';
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import { ConfirmationProvider } from './react/components/form/ConfirmContext';
import { LAST_EVALS_CHECK_KEY } from "./redux/evaluations/epics/syncEpics";

const { store, persistor } = configureStore();

let theme = createMuiTheme({
  palette: {
    primary: {
      main: "#234495",
      light: "#3E599E",
    },
    secondary: {
      main: "#1E9940",
    },
    background: {
      default: grey[400],
    },
    text: {
      primary: "#24292e",
      secondary: "#586069"
    },
  },
  typography: {
    fontFamily: '-apple-system,system-ui,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif'
  },
  spacing: 4,
});

theme = responsiveFontSizes(theme);

// don't bother on localhost
if (process.env.NODE_ENV && process.env.NODE_ENV !== 'development') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [
      new Integrations.BrowserTracing(),
    ],
    release: process.env.REACT_APP_VERSION,
    environment: process.env.REACT_APP_ENVIRONMENT,
    tracesSampleRate: 1.0,
  });
}

ReactDOM.render(
  <Provider store={store}>
    <Router basename="/eval">
      <Route
        render={props => {
          if (props.location.search.includes("code=")){
            return <OAuth {...props} />;
          }

          if (props.location.search.includes("checkEvals=1")){
            localStorage.removeItem(LAST_EVALS_CHECK_KEY);
            window.location.href = window.location.href.replace(/(\?|&)checkEvals=1/, '');
          }

          return (
            <ThemeProvider theme={theme}>
              <ServiceWorkerProvider>                  
                <ConfirmationProvider>
                  <PersistGate loading={null} persistor={persistor}>
                    <App />
                  </PersistGate>
                </ConfirmationProvider>
              </ServiceWorkerProvider>
            </ThemeProvider>
          );
        }}
      />
    </Router>
  </Provider>,
  document.getElementById("root")
);
